import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Collapse,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format } from 'date-fns';

import { useChatStore } from '../stores/chatStore';
import type { ChatMessage, ChartConfig } from '../types';
import EmailComposerModal from '../components/EmailComposerModal';
import { parseEmailDraft, type EmailDraft } from '../utils/parseEmailDraft';
import InlineChart from '../components/chat/InlineChart';
import ChartModal from '../components/chat/ChartModal';

export default function ChatWithUsage() {
  const {
    threads,
    publicThreads,
    isLoadingThreads,
    currentThread,
    currentThreadId,
    isLoadingThread,
    isCurrentThreadOwner,
    isSending,
    streaming,
    error,
    lastFailedMessage,
    loadThreads,
    loadThread,
    sendMessage,
    cancelStream,
    retryLastMessage,
    startNewThread,
    deleteThread,
    toggleVisibility,
    clearError,
  } = useChatStore();

  const [inputValue, setInputValue] = useState('');
  const [enlargedChart, setEnlargedChart] = useState<ChartConfig | null>(null);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  // Email composer modal — opened by the inline affordance under any
  // assistant message that contains a parsed email draft.
  const [emailDraftToCompose, setEmailDraftToCompose] = useState<EmailDraft | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const draftInputRef = useRef('');

  // Input history management (browser-local, like Unix terminal)
  const HISTORY_KEY = 'chat-input-history';
  const MAX_HISTORY = 50;

  const getHistory = useCallback((): string[] => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const addToHistory = useCallback((message: string) => {
    const history = getHistory();
    // Don't add duplicates of the most recent entry
    if (history[0] === message) return;
    const newHistory = [message, ...history].slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
  }, [getHistory]);

  // Load threads on mount
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // Soft prefill from ?user_id=...: seed the input with a usage-focused prompt
  // so the analyst can edit before sending. Consumed once and stripped from the URL.
  // Optional ?user_name= and ?user_email= enrich the prompt for readability.
  const [searchParams, setSearchParams] = useSearchParams();
  const seedUserId = searchParams.get('user_id');
  const seedUserName = searchParams.get('user_name');
  const seedUserEmail = searchParams.get('user_email');
  useEffect(() => {
    if (!seedUserId) return;
    setInputValue((current) => {
      if (current) return current;
      const idLabel = `user_id ${seedUserId}`;
      let subject: string;
      if (seedUserName && seedUserEmail) {
        subject = `${seedUserName} (${seedUserEmail}, ${idLabel})`;
      } else if (seedUserName) {
        subject = `${seedUserName} (${idLabel})`;
      } else if (seedUserEmail) {
        subject = `${seedUserEmail} (${idLabel})`;
      } else {
        subject = idLabel;
      }
      return `Show me usage for ${subject} over the last 30 days.`;
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('user_id');
        next.delete('user_name');
        next.delete('user_email');
        return next;
      },
      { replace: true }
    );
  }, [seedUserId, seedUserName, seedUserEmail, setSearchParams]);

  // Hard-seed from the Onboarding tab's "Send FU" button: it pre-fetches the
  // CS context payload and navigates here with a router-state seed. Auto-submit
  // the seed message + context so the agent's first reply is narration. We
  // consume once and clear the state to prevent replay on remount.
  const location = useLocation();
  const navigate = useNavigate();
  const seedConsumedRef = useRef(false);
  useEffect(() => {
    if (seedConsumedRef.current) return;
    const state = location.state as
      | {
          csFollowUpSeed?: {
            userId: string;
            seedMessage: string;
            csContextSeed: Record<string, unknown>;
          };
        }
      | null;
    const seed = state?.csFollowUpSeed;
    if (!seed) return;

    seedConsumedRef.current = true;
    // Reset the chat to a fresh thread so the synthetic tool turn lands in a
    // new conversation, not as an interruption of whatever was open before.
    startNewThread();
    // Clear the router state immediately so a back-nav into chat doesn't
    // re-fire this. `replace: true` keeps the URL the same.
    navigate(location.pathname, { replace: true, state: null });
    // Defer the send by one tick so startNewThread's state mutation lands
    // first; otherwise sendMessage would attach to the previous thread.
    void Promise.resolve().then(() =>
      sendMessage(seed.seedMessage, seed.csContextSeed)
    );
  }, [location, navigate, sendMessage, startNewThread]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentThread?.messages]);

  // Focus input when thread changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [currentThreadId]);

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isSending) return;

    addToHistory(message);
    setHistoryIndex(-1);
    draftInputRef.current = '';
    setInputValue('');
    await sendMessage(message);
  }, [inputValue, isSending, sendMessage, addToHistory]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'ArrowUp') {
      const history = getHistory();
      if (history.length === 0) return;

      e.preventDefault();
      if (historyIndex === -1) {
        // Save current input as draft before navigating history
        draftInputRef.current = inputValue;
      }
      const newIndex = Math.min(historyIndex + 1, history.length - 1);
      setHistoryIndex(newIndex);
      setInputValue(history[newIndex]);
    } else if (e.key === 'ArrowDown') {
      if (historyIndex === -1) return;

      e.preventDefault();
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      if (newIndex === -1) {
        // Restore draft input
        setInputValue(draftInputRef.current);
      } else {
        const history = getHistory();
        setInputValue(history[newIndex]);
      }
    }
  };

  const handleThreadClick = (threadId: string) => {
    if (threadId !== currentThreadId) {
      loadThread(threadId);
    }
  };

  const handleNewThread = () => {
    startNewThread();
    inputRef.current?.focus();
  };

  const handleDeleteThread = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    setDeletingThreadId(threadId);
    try {
      await deleteThread(threadId);
    } finally {
      setDeletingThreadId(null);
    }
  };

  const handleStop = () => {
    cancelStream();
  };

  const handleRetry = () => {
    retryLastMessage();
  };

  const messages = currentThread?.messages || [];

  // Calculate total tokens for current thread
  const totalTokens = messages.reduce((sum, msg) => sum + (msg.tokenCount || 0), 0);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Thread Sidebar */}
      <Paper
        sx={{
          width: sidebarCollapsed ? 48 : 280,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider',
          transition: 'width 0.2s ease-in-out',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: sidebarCollapsed ? 0.5 : 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 56,
          }}
        >
          {!sidebarCollapsed && (
            <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              Chat History
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!sidebarCollapsed && (
              <Tooltip title="New Chat">
                <IconButton size="small" onClick={handleNewThread} color="primary">
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <IconButton size="small" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Thread List */}
        {!sidebarCollapsed && (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {isLoadingThreads ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : threads.length === 0 && publicThreads.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                No conversations yet.
                <br />
                Start a new chat!
              </Typography>
            </Box>
          ) : (
            <>
              {/* My Threads */}
              {threads.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}
                  >
                    My Threads
                  </Typography>
                  <List dense disablePadding>
                    {threads.map((thread) => {
                      const isDeleting = deletingThreadId === thread.id;
                      const isSelected = thread.id === currentThreadId;
                      return (
                      <ListItem
                        key={thread.id}
                        disablePadding
                        sx={{ position: 'relative' }}
                      >
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleThreadClick(thread.id)}
                          disabled={isDeleting}
                          sx={{ py: 0.75, minHeight: 0, pr: 1 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {thread.isPublic && (
                                  <PublicIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                                )}
                                <Typography
                                  noWrap
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.85rem',
                                    fontWeight: isSelected ? 600 : 400,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {thread.title || 'Untitled Chat'}
                                </Typography>
                              </Box>
                            }
                            secondary={format(new Date(thread.updatedAt), 'MMM d, HH:mm')}
                            secondaryTypographyProps={{
                              variant: 'caption',
                              fontSize: '0.7rem',
                              lineHeight: 1.2,
                            }}
                            sx={{ my: 0 }}
                          />
                        </ListItemButton>
                        {/* Action buttons with gradient background */}
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            opacity: isDeleting ? 1 : 0,
                            transition: 'opacity 0.2s',
                            '.MuiListItem-root:hover &': { opacity: 1 },
                            background: (theme) =>
                              `linear-gradient(to right, transparent, ${
                                isSelected
                                  ? theme.palette.action.selected
                                  : theme.palette.background.paper
                              } 20%)`,
                            pl: 3,
                            pr: 1,
                          }}
                        >
                          {isDeleting ? (
                            <CircularProgress size={18} />
                          ) : (
                            <>
                              <Tooltip title={thread.isPublic ? 'Make private' : 'Share publicly'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVisibility(thread.id);
                                  }}
                                  sx={{
                                    color: thread.isPublic ? 'primary.main' : 'text.secondary',
                                    p: 0.5,
                                  }}
                                >
                                  {thread.isPublic ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteThread(e, thread.id)}
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </ListItem>
                    );
                    })}
                  </List>
                </>
              )}

              {/* Shared Threads */}
              {publicThreads.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 2, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}
                  >
                    Shared Threads
                  </Typography>
                  <List dense disablePadding>
                    {publicThreads.map((thread) => {
                      const isSelected = thread.id === currentThreadId;
                      return (
                        <ListItem key={thread.id} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => handleThreadClick(thread.id)}
                            sx={{ py: 0.75, minHeight: 0, pr: 1 }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PublicIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                                  <Typography
                                    noWrap
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.85rem',
                                      fontWeight: isSelected ? 600 : 400,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {thread.title || 'Untitled Chat'}
                                  </Typography>
                                </Box>
                              }
                              secondary={thread.createdByEmail || format(new Date(thread.updatedAt), 'MMM d, HH:mm')}
                              secondaryTypographyProps={{
                                variant: 'caption',
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                noWrap: true,
                                sx: { pl: 2.5 }, // Align with text after icon
                              }}
                              sx={{ my: 0 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </>
          )}
        </Box>
        )}
      </Paper>

      {/* Chat Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Messages */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {isLoadingThread ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
              }}
            >
              <StorageIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h5" color="text.secondary">
                Chat with Usage Data
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: -1 }}>
                (and customer calls)
              </Typography>
              <Typography color="text.secondary" textAlign="center" maxWidth={500}>
                Ask questions about user activity, usage patterns, product metrics, and customer feedback.
                <br />
                I can query the database, search Linear tickets, and analyze the results for you.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
                {[
                  'How many active users last week?',
                  'Which org has the most queries?',
                  'Do any customers want export features?',
                  'Top 10 users by activity',
                ].map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    variant="outlined"
                    onClick={() => {
                      setInputValue(suggestion);
                      inputRef.current?.focus();
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onEnlargeChart={setEnlargedChart}
                  onOpenEmailDraft={setEmailDraftToCompose}
                />
              ))}
              {isSending && streaming.statusMessage && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {streaming.statusMessage}
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Read-only indicator for shared threads */}
          {currentThread && !isCurrentThreadOwner && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 2,
                maxWidth: 900,
                mx: 'auto',
              }}
            >
              <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                This is a shared thread by {currentThread.createdByEmail} (read-only)
              </Typography>
            </Box>
          )}
          {/* Retry Banner */}
          {error && lastFailedMessage && !isSending && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
                p: 1,
                bgcolor: 'error.dark',
                borderRadius: 2,
                maxWidth: 900,
                mx: 'auto',
              }}
            >
              <Typography variant="body2" color="white">
                Message failed to send
              </Typography>
              <Chip
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
                label="Retry"
                size="small"
                onClick={handleRetry}
                sx={{
                  bgcolor: 'white',
                  color: 'error.dark',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              />
            </Box>
          )}
          {totalTokens > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                ~{totalTokens.toLocaleString()} tokens used
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, maxWidth: 900, mx: 'auto', alignItems: 'center' }}>
            {/* Visibility toggle for owned threads */}
            {currentThread && isCurrentThreadOwner && (
              <Tooltip title={currentThread.isPublic ? 'Make private' : 'Share publicly'}>
                <IconButton
                  onClick={() => toggleVisibility(currentThread.id)}
                  sx={{
                    color: currentThread.isPublic ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {currentThread.isPublic ? <PublicIcon /> : <ShareIcon />}
                </IconButton>
              </Tooltip>
            )}
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={isCurrentThreadOwner ? "Ask about usage data..." : "Read-only thread"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending || !isCurrentThreadOwner}
              inputRef={inputRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
            {isSending ? (
              <Tooltip title="Stop generating">
                <IconButton
                  color="error"
                  onClick={handleStop}
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                >
                  <StopIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!inputValue.trim() || !isCurrentThreadOwner}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&:disabled': { bgcolor: 'action.disabledBackground' },
                }}
              >
                <SendIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* Error Snackbar with Retry */}
      <Snackbar
        open={!!error}
        autoHideDuration={10000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={clearError}
          action={
            lastFailedMessage ? (
              <IconButton
                size="small"
                color="inherit"
                onClick={handleRetry}
                sx={{ ml: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            ) : undefined
          }
        >
          {error}
          {lastFailedMessage && ' — Click to retry'}
        </Alert>
      </Snackbar>

      {/* Chart Modal for enlarged view */}
      <ChartModal
        open={!!enlargedChart}
        config={enlargedChart}
        onClose={() => setEnlargedChart(null)}
      />

      {/* Email composer — preview + copy buttons for agent-drafted FU emails */}
      <EmailComposerModal
        open={!!emailDraftToCompose}
        draft={emailDraftToCompose}
        onClose={() => setEmailDraftToCompose(null)}
      />
    </Box>
  );
}

// Message Bubble Component
interface MessageBubbleProps {
  message: ChatMessage;
  onEnlargeChart?: (config: ChartConfig) => void;
  onOpenEmailDraft?: (draft: EmailDraft) => void;
}

function MessageBubble({ message, onEnlargeChart, onOpenEmailDraft }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isToolCall = !!message.toolName;
  const isStreaming = message.id.startsWith('temp-assistant-');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Detect agent-drafted email blocks and pull them out of the visible bubble.
  // We only treat as a "ready" draft once streaming has finished — partial
  // mid-stream blocks would render an incomplete preview.
  const parsed = useMemo(() => {
    if (isUser || isToolCall || !message.content) {
      return { visibleText: message.content || '', draft: null };
    }
    return parseEmailDraft(message.content);
  }, [isUser, isToolCall, message.content]);
  const draftReady = parsed.draft && !isStreaming;

  const handleCopy = useCallback(async () => {
    if (!message.content) return;
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [message.content]);

  if (isToolCall) {
    const rowCount = (message.toolOutput as { rowCount?: number })?.rowCount ??
                     (message.toolOutput as { count?: number })?.count ?? 0;
    const truncated = (message.toolOutput as { truncated?: boolean })?.truncated;
    const hasChart = !!message.chartConfig && !message.toolError;
    const isLinearSearch = message.toolName === 'search_linear';
    const isLinearCreate = message.toolName === 'create_linear_issue';
    const isLinearList = message.toolName === 'list_linear_issues';
    const isLinearTool = isLinearSearch || isLinearCreate || isLinearList;
    const isAgentConversation = message.toolName === 'get_agent_conversation';

    return (
      <Box sx={{ maxWidth: 800 }}>
        <Paper
          sx={{
            bgcolor: isLinearCreate ? 'rgba(34, 197, 94, 0.1)' : isLinearTool ? 'rgba(99, 102, 241, 0.1)' : 'rgba(139, 92, 246, 0.1)',
            border: '1px solid',
            borderColor: isLinearCreate ? 'rgba(34, 197, 94, 0.3)' : isLinearTool ? 'rgba(99, 102, 241, 0.3)' : 'rgba(139, 92, 246, 0.3)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: isLinearCreate ? 'rgba(34, 197, 94, 0.15)' : isLinearTool ? 'rgba(99, 102, 241, 0.15)' : 'rgba(139, 92, 246, 0.15)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isLinearCreate ? (
                <AddIcon sx={{ fontSize: 16, color: 'success.main' }} />
              ) : isLinearTool ? (
                <SearchIcon sx={{ fontSize: 16, color: 'info.main' }} />
              ) : (
                <StorageIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              )}
              <Typography variant="caption" color={isLinearCreate ? 'success.main' : isLinearTool ? 'info.main' : 'primary.main'} fontWeight={600}>
                {isLinearCreate ? 'Linear Issue Created' : isLinearList ? 'Linear Issues' : isLinearSearch ? 'Linear Search' : isAgentConversation ? 'Agent Conversation' : 'SQL Query Executed'}
              </Typography>
              {!message.toolError && !isLinearCreate && (
                <Typography variant="caption" color="text.secondary">
                  · {rowCount} {isLinearTool ? 'issues' : isAgentConversation ? 'turns' : 'rows'}{truncated ? ' (truncated)' : ''}
                </Typography>
              )}
              {message.toolError && (
                <Typography variant="caption" color="error.main">
                  · Error
                </Typography>
              )}
            </Box>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>

          {/* Inline Chart (always visible when available) */}
          {hasChart && message.chartConfig && (
            <Box sx={{ px: 1.5 }}>
              <InlineChart
                config={message.chartConfig}
                onEnlarge={() => onEnlargeChart?.(message.chartConfig!)}
              />
            </Box>
          )}

          <Collapse in={expanded}>
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              {message.toolInput && (message.toolInput as { query?: string }).query && (
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {(message.toolInput as { query: string }).query}
                </Box>
              )}
              {message.toolError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {message.toolError}
                </Alert>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Paper
        elevation={isUser ? 0 : 1}
        sx={{
          p: 2,
          maxWidth: isUser ? 600 : 800,
          bgcolor: isUser ? 'transparent' : 'background.paper',
          background: isUser
            ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.95) 0%, rgba(99, 46, 190, 0.9) 100%)'
            : undefined,
          color: isUser ? 'white' : 'text.primary',
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : 2,
          borderTopLeftRadius: isUser ? 2 : 0,
          position: 'relative',
          ...(isUser && {
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
            border: '1px solid rgba(167, 139, 250, 0.3)',
          }),
          ...(!isUser && {
            '&:hover .copy-button': {
              opacity: 1,
            },
          }),
        }}
      >
        {/* Copy button for AI messages */}
        {!isUser && message.content && !isStreaming && (
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton
              className="copy-button"
              size="small"
              onClick={handleCopy}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0,
                transition: 'opacity 0.2s',
                color: copied ? 'success.main' : 'text.secondary',
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {isUser ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
              '& pre': {
                bgcolor: 'rgba(0,0,0,0.2)',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.8rem',
              },
              '& code': {
                bgcolor: 'rgba(0,0,0,0.15)',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.85em',
                fontFamily: 'monospace',
              },
              '& pre code': {
                bgcolor: 'transparent',
                p: 0,
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                my: 1,
                fontSize: '0.85rem',
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'rgba(0,0,0,0.1)',
                fontWeight: 600,
              },
              '& ul, & ol': {
                pl: 3,
                my: 1,
              },
              '& li': {
                mb: 0.5,
              },
              '& h1, & h2, & h3': {
                mt: 2,
                mb: 1,
                '&:first-of-type': { mt: 0 },
              },
            }}
          >
            {message.content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{parsed.visibleText || message.content}</ReactMarkdown>
            ) : isStreaming ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 16,
                  bgcolor: 'primary.main',
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 50%': { opacity: 1 },
                    '51%, 100%': { opacity: 0 },
                  },
                }}
              />
            ) : null}
            {isStreaming && message.content && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 16,
                  bgcolor: 'primary.main',
                  ml: 0.5,
                  verticalAlign: 'text-bottom',
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 50%': { opacity: 1 },
                    '51%, 100%': { opacity: 0 },
                  },
                }}
              />
            )}
            {draftReady && parsed.draft && onOpenEmailDraft && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<MailOutlineIcon fontSize="small" />}
                  onClick={() => onOpenEmailDraft(parsed.draft!)}
                  sx={{ textTransform: 'none' }}
                >
                  Open email composer
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1.5, verticalAlign: 'middle' }}
                >
                  Subject: <strong>{parsed.draft.subject}</strong> · To: {parsed.draft.to}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
